import {
  Box,
  Text,
  Badge
} from "@chakra-ui/react";
import { parseISO } from "date-fns";
import { DayView } from "../../types/Calendar";
import { ScheduleCard } from "../ScheduleCard";
import { Event } from "../../services/hooks/useClassesList";

interface DayColumnProps {
  day: DayView;
  isMobile?: boolean;
}

// Constantes de configuração do calendário
const CALENDAR_START_HOUR = 6; // Início do calendário às 06:00
const HOUR_HEIGHT_PX = 90; // Altura visual de cada hora em pixels
const CARD_VISUAL_DURATION_MINUTES = 60; // Duração visual de cada card em minutos
const SCALE_FACTOR = HOUR_HEIGHT_PX / 60; // Fator de escala: 90px/60min = 1.5

const calculateTopPosition = (dateISO: string): number => {
  const date = parseISO(dateISO);
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  const offsetMinutes = CALENDAR_START_HOUR * 60;
  
  return (totalMinutes - offsetMinutes) * SCALE_FACTOR;
};

const calculateEventHeight = (startDateISO: string, endDateISO: string): number => {
  const startDate = parseISO(startDateISO);
  const endDate = parseISO(endDateISO);
  const durationInMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
  
  return durationInMinutes * SCALE_FACTOR;
};

const eventsOverlap = (event1: Event, event2: Event): boolean => {
  const start1 = parseISO(event1.start_date).getTime();
  const start2 = parseISO(event2.start_date).getTime();
  const visualEnd1 = start1 + (CARD_VISUAL_DURATION_MINUTES * 60 * 1000);
  const visualEnd2 = start2 + (CARD_VISUAL_DURATION_MINUTES * 60 * 1000);
  
  return start1 < visualEnd2 && start2 < visualEnd1;
};

const calculateEventLayout = (events: Event[]) => {
  const layout: Array<{ event: Event; column: number; totalColumns: number }> = [];

  const sortedEvents = [...events].sort((a, b) => 
    parseISO(a.start_date).getTime() - parseISO(b.start_date).getTime()
  );
  
  sortedEvents.forEach((event) => {
    const overlappingEvents = layout.filter(item => 
      eventsOverlap(item.event, event)
    );
    
    if (overlappingEvents.length === 0) {
      layout.push({ event, column: 0, totalColumns: 1 });
    } else {
      const usedColumns = overlappingEvents.map(item => item.column);
      const column = findAvailableColumn(usedColumns);
      const totalColumns = Math.max(...overlappingEvents.map(item => item.totalColumns), column + 1);

      overlappingEvents.forEach(item => {
        item.totalColumns = totalColumns;
      });
      
      layout.push({ event, column, totalColumns });
    }
  });
  
  return layout;
};

const findAvailableColumn = (usedColumns: number[]): number => {
  for (let column = 0; ; column++) {
    if (!usedColumns.includes(column)) {
      return column;
    }
  }
};

export function DayColumn({ day, isMobile = false }: DayColumnProps) {
  const eventLayout = calculateEventLayout(day.events);
  
  return (
    <Box
      bg="gray.50"
      p={0.5}
      h="full"
      w="100%"
      display="flex"
      flexDirection="column"
    >
      {isMobile && (
        <Box 
          mb={4} 
          textAlign="center"
          pb={2}
          borderBottom="1px"
          borderColor="gray.200"
        >
          <Text fontSize="small" color={day.isToday ? "blue.500" : "blue.900"}>
            <strong>{day.dayName}</strong>
          </Text>

          <Text fontSize="sm" color={day.isToday ? "blue.500" : "gray.700"}>
            <strong>{day.formattedDate}</strong>
          </Text>

          {day.events.length > 0 && (
            <Badge colorScheme="green" fontSize="xs" mt={1}>
              {day.events.length} aula{day.events.length > 1 ? 's' : ''}
            </Badge>
          )}
        </Box>
      )}

      <Box
        flex="1"
        position="relative"
        minH="1530px"
      >
        {eventLayout.map(({ event, column, totalColumns }) => {
          const topPosition = calculateTopPosition(event.start_date);
          const eventHeight = calculateEventHeight(event.start_date, event.end_date);
          const hasOverlap = totalColumns > 1;
          const widthPercent = 100 / totalColumns;
          const leftPercent = column * widthPercent;
          
          return (
            <Box 
              key={event.id} 
              position="absolute"
              top={`${topPosition}px`}
              left={`calc(${leftPercent}% + 4px)`}
              w={`calc(${widthPercent}% - 4px)`}
              h={`${eventHeight}px`}
              minH="50px"
            >
              <ScheduleCard
                eventId={event.id}
                waiting={event.waiting}
                title={event.title}
                levels={hasOverlap ? undefined : event.levels}
                description={event.description}
                date={event.startDateFormated}
                startHour={event.startHourFormated}
                endHour={event.endHourFormatted}
                duration={event.duration}
                credit={event.credit}
                eventHasSubject={event.request_subject}
                startDateISO={event.start_date}
                dayOfWeek={event.dayOfWeek}
                setHighlight={event.has_highlight}
                forTeachers={event.for_teachers}
                descriptionFormatted={event.description_formatted || event.description}
                classSubject={event.class_subject_id}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
